/**
 * Portal onboardingu pracownika (spec 04) — publiczny, bez logowania.
 *
 * Ekran wynika ze STANU PROCESU z serwera, nie z kliknięć: odświeżenie strony,
 * powrót po tygodniu i wejście z linku wznowienia trafiają w to samo miejsce.
 * Makietowe skróty („Podgląd kolejnego etapu (demo)", podpowiedź z adresem)
 * nie mają tu odpowiednika [D19] — dalej prowadzi wyłącznie realna akcja.
 *
 * Odstępstwa od makiety, świadome:
 *  - oświadczenie o informacji dodatkowej jest WYMAGANE, a o regulaminie —
 *    tylko przy regulaminie opublikowanym w systemie i umowie o pracę [D8];
 *  - w kroku danych jest oświadczenie o zapoznaniu z klauzulą RODO, nie zgoda [D101];
 *  - „Ważne do" to pełna data (API i przypomnienia potrzebują dnia, nie mies.);
 *  - „Pobierz skierowanie" / „Pobierz PDF" są widocznie wyłączone z notą [D21];
 *  - doszedł ekran oczekiwania na BHP i wątek pytania do działu HR [D11].
 */
import { useId, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Check,
  Checkbox,
  Clock,
  Document,
  FormField,
  InfoBanner,
  Input,
  Select,
  Logo,
  Modal,
  ProcessTimeline,
  ProgressBar,
  ShieldCheckIcon,
  Spinner,
  cx,
} from '@k1p4/ui'
import {
  NFZ_BRANCHES,
  TAX_OFFICES,
  emailSchema,
  ibanPlSchema,
  parseStreetLine,
  peselSchema,
  phonePlSchema,
  postalCodeSchema,
  isEmploymentContract,
  shiftDay,
  warsawDay,
  workingTimeFraction,
} from '@k1p4/shared'
import { formatPLN } from '@k1p4/i18n'
import {
  ApiError,
  api,
  apiToday,
  type OnboardingDocumentKey,
  type OnboardingSessionResponse,
  type OnboardingStage,
} from '../../api/client'
import { LanguageSwitcher } from '../../components/LanguageSwitcher'
import styles from './Join.module.css'
import { greetingName, useFormat } from '../../format'
import { useDocumentTitle } from '../../components/useDocumentTitle'

/** Siedem faz z makiety; etapy kanoniczne mapujemy na nie w `PHASE_OF_STAGE`. */
const PHASES = [
  { key: 'join', owner: 'employee' },
  { key: 'data', owner: 'employee' },
  { key: 'hr', owner: 'hr' },
  { key: 'terms', owner: 'hrEmployee' },
  { key: 'medical', owner: 'employee' },
  { key: 'bhp', owner: 'bhp' },
  { key: 'active', owner: 'system' },
] as const

const PHASE_OF_STAGE: Record<OnboardingStage, number> = {
  invited: 1,
  email_verified: 2,
  data_submitted: 3,
  data_approved: 3,
  terms_prepared: 4,
  terms_accepted: 5,
  referral_issued: 5,
  medical_approved: 6,
  bhp_confirmed: 6,
  active: 7,
}

type Screen =
  | 'join'
  | 'wizard'
  | 'waitingHr'
  | 'terms'
  | 'waitingReferral'
  | 'medical'
  | 'waitingBhp'
  | 'done'

const SCREEN_OF_STAGE: Record<OnboardingStage, Screen> = {
  invited: 'join',
  email_verified: 'wizard',
  data_submitted: 'waitingHr',
  data_approved: 'waitingHr',
  terms_prepared: 'terms',
  terms_accepted: 'waitingReferral',
  referral_issued: 'medical',
  medical_approved: 'waitingBhp',
  bhp_confirmed: 'waitingBhp',
  active: 'done',
}

const WIZARD_STEPS = 4

interface WizardValues {
  pesel: string
  birthDate: string
  privateEmail: string
  phone: string
  addressStreet: string
  addressCityZip: string
  taxOfficeCode: string
  nfzBranch: string
  iban: string
  bankName: string
  contactName: string
  contactPhone: string
  relation: string
}

const EMPTY_WIZARD: WizardValues = {
  pesel: '',
  birthDate: '',
  privateEmail: '',
  phone: '',
  addressStreet: '',
  addressCityZip: '',
  taxOfficeCode: '',
  nfzBranch: '',
  iban: '',
  bankName: '',
  contactName: '',
  contactPhone: '',
  relation: '',
}

/** Puste pole wysyłamy jako `undefined` — API rozróżnia „brak" od pustego stringa. */
function optional(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

type WizardField = keyof WizardValues

/** Pola podkroków w kolejności z ekranu — fokus trafia na pierwsze z błędem. */
const STEP_FIELDS: readonly (readonly WizardField[])[] = [
  ['pesel', 'birthDate', 'privateEmail', 'phone', 'addressStreet', 'addressCityZip'],
  ['taxOfficeCode', 'nfzBranch', 'iban', 'bankName'],
  ['contactName', 'contactPhone', 'relation'],
  [],
]

/** Kontakt awaryjny ma w API własne nazwy pól. */
const API_FIELD: Readonly<Record<string, WizardField>> = { name: 'contactName', phone: 'contactPhone' }

/**
 * Pola podkroku z odmowy API (`issues`) w kolejności z ekranu. Pole, którego
 * kreator nie pokazuje, zostaje przy ogólnym komunikacie nad formularzem.
 */
function fieldsFromIssues(step: number, issues: readonly string[]): WizardField[] {
  const named = new Set(issues.map((issue) => (step === 2 ? (API_FIELD[issue] ?? issue) : issue)))
  return (STEP_FIELDS[step] ?? []).filter((field) => named.has(field))
}

/** Puste pole jest dozwolone — odrzucamy tylko wpis w złym formacie. */
function rejects(value: string, valid: (trimmed: string) => boolean): boolean {
  const trimmed = value.trim()
  return trimmed.length > 0 && !valid(trimmed)
}

/**
 * Sprawdzenie przed zapisem tymi samymi schematami z `@k1p4/shared`, którymi
 * odmawia API — kandydat widzi błąd przy polu, zanim formularz wyjdzie, a nie
 * ogólne „format jest nieprawidłowy" po odpowiedzi serwera (grupa 117).
 */
function invalidFields(step: number, values: WizardValues): WizardField[] {
  const checks: Partial<Record<WizardField, boolean>> =
    step === 0
      ? {
          pesel: rejects(values.pesel, (value) => peselSchema.safeParse(value).success),
          privateEmail: rejects(values.privateEmail, (value) => emailSchema.safeParse(value).success),
          phone: rejects(values.phone, (value) => phonePlSchema.safeParse(value).success),
          /* Adres, którego nie da się rozbić na pola ZUS ZUA, nie wchodzi do akt (#7). */
          addressStreet: rejects(values.addressStreet, (value) => parseStreetLine(value) !== null),
          /* Jak w API: kod pocztowy otwiera pole, miejscowość idzie po nim. */
          addressCityZip: rejects(
            values.addressCityZip,
            (value) => postalCodeSchema.safeParse(value.split(/\s+/)[0] ?? '').success,
          ),
        }
      : step === 1
        ? { iban: rejects(values.iban, (value) => ibanPlSchema.safeParse(value).success) }
        : step === 2
          ? { contactPhone: rejects(values.contactPhone, (value) => phonePlSchema.safeParse(value).success) }
          : {}
  return (STEP_FIELDS[step] ?? []).filter((field) => checks[field] === true)
}

export function Join() {
  useDocumentTitle()
  const fmt = useFormat()
  const { t } = useTranslation()
  const { code } = useParams<{ code?: string }>()
  const [searchParams] = useSearchParams()
  const resumeToken = searchParams.get('s') ?? undefined
  const queryClient = useQueryClient()

  const session = useQuery<OnboardingSessionResponse>({
    queryKey: ['onboarding', 'session'],
    queryFn: () => api.onboardingSession(resumeToken),
    retry: false,
    staleTime: 0,
  })

  const [documentKey, setDocumentKey] = useState<OnboardingDocumentKey | null>(null)

  const noSession = session.isError && session.error instanceof ApiError && session.error.status === 401
  const data = session.data ?? null

  /*
   * Stopka od pierwszego ekranu (spec 04, Fala 6 grupa 33): zanim powstanie
   * sesja, stanowisko, dział i datę rozpoczęcia zna podgląd zaproszenia — to
   * samo zapytanie co nagłówek ekranu 0, więc bez drugiego żądania.
   */
  const preview = useQuery({
    queryKey: ['onboarding', 'invitation', code],
    queryFn: () => api.invitationPreview(code!),
    enabled: code !== undefined && data === null,
    retry: false,
  })
  const footer = data
    ? data.employee
    : preview.data
      ? {
          department: preview.data.departmentName,
          position: preview.data.position,
          startDate: preview.data.startDate,
        }
      : null
  const screen: Screen = data ? SCREEN_OF_STAGE[data.stage] : 'join'
  const phase = data ? PHASE_OF_STAGE[data.stage] : 1
  /* Wizard należy do fazy „Dane osobowe", nawet zanim dane pojadą do HR. */
  const activePhase = screen === 'wizard' ? 2 : phase

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['onboarding', 'session'] })

  if (session.isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label={t('common.loading')} />
      </div>
    )
  }

  if (session.isError && !noSession) {
    return (
      <div className={styles.loading}>
        <InfoBanner tone="danger">{t('join.expired')}</InfoBanner>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <aside className={styles.rail}>
        <span className={styles.railGlow} aria-hidden="true" />
        <div className={styles.railContent}>
          <Logo size={38} variant="primary" withWordmark tagline={t('join.rail.tagline')} withTagline />

          <div>
            <p className={styles.railKicker}>{t('join.rail.kicker')}</p>
            <p className={styles.railPhase}>{t(`join.phases.${PHASES[activePhase - 1]!.key}`)}</p>
            <p className={styles.railCounter}>
              {t('join.rail.phaseCounter', { current: activePhase, total: PHASES.length })}
            </p>
          </div>

          <ProcessTimeline
            className={styles.railTimeline}
            current={activePhase}
            blocked={data?.sideState === 'rejected'}
            items={PHASES.map((item) => ({
              key: item.key,
              label: t(`join.phases.${item.key}`),
              owner: t(`join.owners.${item.owner}`),
            }))}
          />

          <p className={styles.railTrust}>
            <span className={styles.railTrustIcon} aria-hidden="true">
              <ShieldCheckIcon size={16} />
            </span>
            {t('join.rail.security')}
          </p>
        </div>
      </aside>

      <main className={styles.main}>
        <div className={styles.topbar}>
          <LanguageSwitcher />
        </div>

        <div className={styles.content}>
          {noSession || screen === 'join' ? (
            <JoinScreen code={code ?? null} onJoined={refresh} />
          ) : null}

          {data && screen === 'wizard' ? (
            <WizardScreen data={data} onDone={refresh} onOpenDocument={setDocumentKey} />
          ) : null}

          {data && screen === 'waitingHr' ? (
            <WaitingScreen
              title={t('join.waitingHr.title')}
              body={t('join.waitingHr.body')}
              messages={data.messages}
            />
          ) : null}

          {data && screen === 'terms' ? (
            <TermsScreen data={data} onChanged={refresh} onOpenDocument={setDocumentKey} />
          ) : null}

          {data && screen === 'waitingReferral' ? (
            <WaitingScreen
              title={t('join.waitingReferral.title')}
              body={t('join.waitingReferral.body')}
              messages={data.messages}
            />
          ) : null}

          {data && screen === 'medical' ? (
            <MedicalScreen
              referralIssuedAt={data.medical?.referralIssuedAt ?? null}
              startDate={data.employee.startDate}
              onDone={refresh}
            />
          ) : null}

          {data && screen === 'waitingBhp' ? (
            <WaitingScreen
              title={t('join.waitingBhp.title')}
              body={t('join.waitingBhp.body')}
              messages={data.messages}
            />
          ) : null}

          {data && screen === 'done' ? <DoneScreen firstName={data.employee.firstName} /> : null}
        </div>

        {footer ? (
          <footer className={styles.footer}>
            <span className={styles.footerCell}>
              <span className={styles.footerLabel}>{t('join.footer.department')}</span>
              <span className={styles.footerValue}>
                {footer.department ?? t('join.footer.none')}
              </span>
            </span>
            <span className={styles.footerCell}>
              <span className={styles.footerLabel}>{t('join.footer.position')}</span>
              <span className={styles.footerValue}>
                {footer.position ?? t('join.footer.none')}
              </span>
            </span>
            <span className={styles.footerCell}>
              <span className={styles.footerLabel}>{t('join.footer.startDate')}</span>
              <span className={styles.footerValue}>
                {footer.startDate ? fmt.date(footer.startDate, 'long') : t('join.footer.none')}
              </span>
            </span>
          </footer>
        ) : null}
      </main>

      <DocumentModal documentKey={documentKey} onClose={() => setDocumentKey(null)} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ekran 0 — dołączenie i weryfikacja adresu
// ---------------------------------------------------------------------------

function JoinScreen({ code, onJoined }: { code: string | null; onJoined: () => void }) {
  const { t } = useTranslation()
  const emailId = useId()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  /* Nagłówek z nazwą firmy i adresatem — widoczny dla posiadacza kodu. */
  const invitation = useQuery({
    queryKey: ['onboarding', 'invitation', code],
    queryFn: () => api.invitationPreview(code!),
    enabled: code !== null,
    retry: false,
  })

  const join = useMutation({
    mutationFn: () => api.onboardingJoin(code ?? '', email.trim()),
    onSuccess: onJoined,
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'EMAIL_MISMATCH'
            ? 'join.step0.errorMismatch'
            : caught.code === 'EMAIL_TAKEN'
              ? 'join.step0.errorTaken'
              : caught.code === 'PROCESS_FINISHED'
                ? 'join.step0.errorFinished'
                : 'join.step0.errorInvalid',
        )
      } else {
        setError('join.errorGeneric')
      }
    },
  })

  /*
   * Dlaczego kod nie otwiera procesu (Fala 6, grupa 30): literówka w linku,
   * kod po terminie i proces już zakończony to trzy różne rady.
   */
  const previewError =
    invitation.error instanceof ApiError &&
    (invitation.error.code === 'INVITE_NOT_FOUND' || invitation.error.code === 'INVITE_INVALID')
      ? 'join.step0.errorNotFound'
      : invitation.error instanceof ApiError && invitation.error.code === 'PROCESS_FINISHED'
        ? 'join.step0.errorFinished'
        : invitation.error instanceof ApiError && invitation.error.status === 410
          ? 'join.step0.errorInvalid'
          : 'join.errorGeneric'
  /* Jedyna akcja ekranu — przycisk z celem dotyku 44 px, nie link w wierszu tekstu (przegląd Fali 6). */
  const signInLink = (
    <Link to="/login">
      <Button variant="secondary" size="sm">
        {t('join.step0.signIn')}
      </Button>
    </Link>
  )

  return (
    <>
      <p className={styles.eyebrow}>{t('join.step0.eyebrow')}</p>
      <h1 className={styles.title}>{t('join.step0.title')}</h1>

      {invitation.data ? (
        <Card className={styles.orgCard} pad="md">
          <Avatar kind="organization" name={invitation.data.organizationName} size={46} />
          <span>
            <p className={styles.orgName}>{invitation.data.organizationName}</p>
            <p className={styles.orgInvitee}>
              {t('join.step0.invitedFor')}{' '}
              <span className={styles.orgInviteeName}>
                {invitation.data.firstName} {invitation.data.lastName}
              </span>
            </p>
          </span>
        </Card>
      ) : null}

      <p className={styles.lead}>{t('join.step0.lead')}</p>

      {code === null ? (
        <InfoBanner tone="warning">{t('join.step0.noCode')}</InfoBanner>
      ) : invitation.isError ? (
        <>
          <InfoBanner tone="danger">{t(previewError)}</InfoBanner>
          {previewError === 'join.step0.errorFinished' ? signInLink : null}
        </>
      ) : (
        <>
          {error !== null ? (
            <InfoBanner tone="danger" className={styles.banner}>
              {t(error)}
            </InfoBanner>
          ) : null}
          {error === 'join.step0.errorFinished' ? signInLink : null}

          <FormField label={t('join.step0.emailLabel')} htmlFor={emailId}>
            <Input
              type="email"
              autoComplete="email"
              placeholder={t('join.step0.emailPlaceholder')}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setError(null)
              }}
            />
          </FormField>

          <Button
            size="lg"
            className={styles.fullButton}
            disabled={join.isPending || !email.includes('@')}
            onClick={() => join.mutate()}
          >
            {join.isPending ? <Spinner label={t('common.loading')} /> : t('join.step0.submit')}
          </Button>
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Ekran 1 — wizard danych (4 podkroki)
// ---------------------------------------------------------------------------

function WizardScreen({
  data,
  onDone,
  onOpenDocument,
}: {
  data: OnboardingSessionResponse
  onDone: () => void
  onOpenDocument: (key: OnboardingDocumentKey) => void
}) {
  const { t } = useTranslation()
  const ids = {
    pesel: useId(),
    birthDate: useId(),
    privateEmail: useId(),
    phone: useId(),
    addressStreet: useId(),
    addressCityZip: useId(),
    taxOfficeCode: useId(),
    nfzBranch: useId(),
    iban: useId(),
    bankName: useId(),
    contactName: useId(),
    contactPhone: useId(),
    relation: useId(),
  }
  const [step, setStep] = useState(0)
  /*
   * Szkic z serwera wypełnia formularz przy montowaniu — PESEL i IBAN nigdy
   * nie wracają z API, więc te dwa pola startują puste także po powrocie.
   */
  const [values, setValues] = useState<WizardValues>(() => ({
    ...EMPTY_WIZARD,
    birthDate: data.draft.personal?.birthDate ?? '',
    privateEmail: data.draft.personal?.privateEmail ?? '',
    phone: data.draft.personal?.phone ?? '',
    addressStreet: data.draft.personal?.addressStreet ?? '',
    addressCityZip: data.draft.personal?.addressCityZip ?? '',
    taxOfficeCode: data.draft.tax?.taxOfficeCode ?? '',
    nfzBranch: data.draft.tax?.nfzBranch ?? '',
    bankName: data.draft.bank?.bankName ?? '',
    contactName: data.draft.emergency?.name ?? '',
    contactPhone: data.draft.emergency?.phone ?? '',
    relation: data.draft.emergency?.relation ?? '',
  }))
  const [rodo, setRodo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* Pola z błędem formatu — z kontroli przed zapisem albo z `issues` odmowy API. */
  const [fieldErrors, setFieldErrors] = useState<readonly WizardField[]>([])

  const set = (patch: Partial<WizardValues>) => {
    setValues((current) => ({ ...current, ...patch }))
    setError(null)
    setFieldErrors((current) => current.filter((field) => !(field in patch)))
  }

  /** Oznacza pola i przenosi fokus na pierwsze z nich (pole jest już na ekranie). */
  const reveal = (fields: readonly WizardField[]): void => {
    setFieldErrors(fields)
    const first = fields[0]
    if (first !== undefined) document.getElementById(ids[first])?.focus()
  }

  /** Błąd przy polu zamiast podpowiedzi — komunikat mówi, jakiego formatu pole oczekuje. */
  const feedback = (field: WizardField, hint?: string): { error?: string; hint?: string } =>
    fieldErrors.includes(field) ? { error: t(`join.wizard.errors.${field}`) } : { hint }

  const goTo = (target: number): void => {
    setFieldErrors([])
    setStep(target)
  }

  const save = useMutation({
    mutationFn: async (target: number) => {
      if (step === 0) {
        await api.onboardingSavePersonal({
          pesel: optional(values.pesel),
          birthDate: optional(values.birthDate),
          privateEmail: optional(values.privateEmail),
          phone: optional(values.phone),
          addressStreet: optional(values.addressStreet),
          addressCityZip: optional(values.addressCityZip),
        })
      } else if (step === 1) {
        await api.onboardingSaveTaxBank({
          taxOfficeCode: optional(values.taxOfficeCode),
          nfzBranch: optional(values.nfzBranch),
          iban: optional(values.iban),
          bankName: optional(values.bankName),
        })
      } else if (step === 2) {
        await api.onboardingSaveEmergency({
          name: optional(values.contactName),
          phone: optional(values.contactPhone),
          relation: optional(values.relation),
        })
      } else {
        await api.onboardingSubmit()
      }
      return target
    },
    onSuccess: (target) => {
      if (target >= WIZARD_STEPS) onDone()
      else goTo(target)
    },
    onError: (caught, target) => {
      /*
       * Etap danych minął w innej karcie albo dział HR już je przyjął (#2) —
       * serwer nic nie zapisał. Odświeżenie sesji przenosi na ekran
       * właściwego etapu, a nota mówi, dlaczego formularz zniknął.
       */
      if (caught instanceof ApiError && caught.code === 'DATA_LOCKED') {
        setError('join.wizard.locked')
        onDone()
        return
      }
      if (caught instanceof ApiError && caught.code === 'VALIDATION_ERROR') {
        const fields = fieldsFromIssues(target - 1, caught.issues)
        reveal(fields)
        /* Bez pola na ekranie zostaje ogólny komunikat nad formularzem. */
        if (fields.length === 0) setError('join.wizard.invalid')
        return
      }
      setError('join.errorGeneric')
    },
  })

  function next(): void {
    if (step === 0) {
      if (!rodo) {
        setError('join.wizard.rodo.error')
        return
      }
    }
    const invalid = invalidFields(step, values)
    if (invalid.length > 0) {
      reveal(invalid)
      return
    }
    save.mutate(step + 1)
  }

  const stepKey = (['personal', 'taxBank', 'emergency', 'summary'] as const)[step]!

  return (
    <>
      <p className={styles.eyebrow}>
        {t('join.wizard.eyebrow', { current: step + 1, total: WIZARD_STEPS })}
      </p>
      <ProgressBar
        className={styles.progress}
        value={step + 1}
        max={WIZARD_STEPS}
        label={t('join.wizard.progressLabel')}
      />
      <h1 className={styles.title}>{t(`join.wizard.${stepKey}.title`)}</h1>
      <p className={styles.lead}>{t(`join.wizard.${stepKey}.description`)}</p>

      {error !== null || fieldErrors.length > 0 ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error ?? 'join.wizard.invalid')}
        </InfoBanner>
      ) : null}

      {step === 0 ? (
        <>
          <Card className={styles.soonCard} pad="md">
            <div className={styles.soonHead}>
              <p className={styles.soonTitle}>{t('join.wizard.mobywatel.title')}</p>
              <Badge tone="warning">{t('soon.title')}</Badge>
            </div>
            <p className={styles.soonBody}>{t('join.wizard.mobywatel.body')}</p>
          </Card>

          <div className={styles.fieldGrid}>
            <FormField
              label={t('join.wizard.fields.pesel')}
              htmlFor={ids.pesel}
              {...feedback('pesel', data.draft.personal?.hasPesel ? t('join.wizard.fields.saved') : undefined)}
            >
              <Input
                inputMode="numeric"
                placeholder={t('join.wizard.placeholders.pesel')}
                value={values.pesel}
                onChange={(event) => set({ pesel: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('join.wizard.fields.birthDate')}
              htmlFor={ids.birthDate}
              {...feedback('birthDate')}
            >
              <Input
                type="date"
                value={values.birthDate}
                onChange={(event) => set({ birthDate: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('join.wizard.fields.privateEmail')}
              htmlFor={ids.privateEmail}
              {...feedback('privateEmail')}
            >
              <Input
                type="email"
                placeholder={t('join.wizard.placeholders.privateEmail')}
                value={values.privateEmail}
                onChange={(event) => set({ privateEmail: event.target.value })}
              />
            </FormField>
            <FormField label={t('join.wizard.fields.phone')} htmlFor={ids.phone} {...feedback('phone')}>
              <Input
                placeholder={t('join.wizard.placeholders.phone')}
                value={values.phone}
                onChange={(event) => set({ phone: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('join.wizard.fields.addressStreet')}
              htmlFor={ids.addressStreet}
              {...feedback('addressStreet', t('validation.addressStreet.hint'))}
            >
              <Input
                placeholder={t('join.wizard.placeholders.addressStreet')}
                value={values.addressStreet}
                onChange={(event) => set({ addressStreet: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('join.wizard.fields.addressCityZip')}
              htmlFor={ids.addressCityZip}
              {...feedback('addressCityZip')}
            >
              <Input
                placeholder={t('join.wizard.placeholders.addressCityZip')}
                value={values.addressCityZip}
                onChange={(event) => set({ addressCityZip: event.target.value })}
              />
            </FormField>
          </div>

          <Card className={styles.consentCard} pad="md">
            <Checkbox
              checked={rodo}
              onChange={(event) => {
                setRodo(event.target.checked)
                setError(null)
              }}
              label={
                <span>
                  {t('join.wizard.rodo.textBefore')}
                  <button
                    type="button"
                    className={styles.inlineLink}
                    onClick={() => onOpenDocument('rodo')}
                  >
                    {t('join.wizard.rodo.link')}
                  </button>
                  {t('join.wizard.rodo.textAfter')}{' '}
                  <span className={styles.required}>{t('join.wizard.rodo.required')}</span>
                </span>
              }
            />
          </Card>
        </>
      ) : null}

      {step === 1 ? (
        <div className={styles.fieldGrid}>
          {/*
            Urząd skarbowy wybiera się z wykazu Ministerstwa Finansów: jego
            czterocyfrowy kod wchodzi do nagłówka informacji PIT-11 składanej
            elektronicznie, więc nazwa własnymi słowami by go nie zastąpiła
            (Etap 27).
          */}
          <FormField
            label={t('join.wizard.fields.taxOffice')}
            htmlFor={ids.taxOfficeCode}
            {...feedback('taxOfficeCode', t('join.wizard.fields.taxOfficeHint'))}
          >
            <Select
              id={ids.taxOfficeCode}
              value={values.taxOfficeCode}
              onChange={(event) => set({ taxOfficeCode: event.target.value })}
            >
              <option value="">{t('join.wizard.fields.taxOfficePlaceholder')}</option>
              {TAX_OFFICES.map((office) => (
                <option key={office.code} value={office.code}>
                  {office.name}
                </option>
              ))}
            </Select>
          </FormField>
          {/*
            Oddział NFZ wybiera się z wykazu ZUS: jego trzyznakowy kod wchodzi do
            bloku VII formularza ZUS ZUA, więc opis własnymi słowami by go nie
            zastąpił (Etap 16).
          */}
          <FormField
            label={t('join.wizard.fields.nfzBranch')}
            htmlFor={ids.nfzBranch}
            {...feedback('nfzBranch', t('join.wizard.fields.nfzBranchHint'))}
          >
            <Select
              id={ids.nfzBranch}
              value={values.nfzBranch}
              onChange={(event) => set({ nfzBranch: event.target.value })}
            >
              <option value="">{t('join.wizard.fields.nfzBranchPlaceholder')}</option>
              {NFZ_BRANCHES.map((branch) => (
                <option key={branch.code} value={branch.code}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('join.wizard.fields.iban')}
            htmlFor={ids.iban}
            {...feedback('iban', data.draft.bank?.hasIban ? t('join.wizard.fields.saved') : undefined)}
          >
            <Input
              placeholder={t('join.wizard.placeholders.iban')}
              value={values.iban}
              onChange={(event) => set({ iban: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('join.wizard.fields.bankName')}
            htmlFor={ids.bankName}
            {...feedback('bankName')}
          >
            <Input
              value={values.bankName}
              onChange={(event) => set({ bankName: event.target.value })}
            />
          </FormField>
        </div>
      ) : null}

      {step === 2 ? (
        <div className={styles.fieldGrid}>
          <FormField
            label={t('join.wizard.fields.contactName')}
            htmlFor={ids.contactName}
            {...feedback('contactName')}
          >
            <Input
              value={values.contactName}
              onChange={(event) => set({ contactName: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('join.wizard.fields.contactPhone')}
            htmlFor={ids.contactPhone}
            {...feedback('contactPhone')}
          >
            <Input
              placeholder={t('join.wizard.placeholders.phone')}
              value={values.contactPhone}
              onChange={(event) => set({ contactPhone: event.target.value })}
            />
          </FormField>
          <FormField
            className={styles.fullWidth}
            label={t('join.wizard.fields.relation')}
            htmlFor={ids.relation}
            {...feedback('relation')}
          >
            <Input
              placeholder={t('join.wizard.placeholders.relation')}
              value={values.relation}
              onChange={(event) => set({ relation: event.target.value })}
            />
          </FormField>
        </div>
      ) : null}

      {step === 3 ? (
        <Card className={styles.summaryCard} pad="lg">
          <p className={styles.summaryEyebrow}>{t('join.wizard.summary.eyebrow')}</p>
          <p className={styles.summaryBody}>{t('join.wizard.summary.body')}</p>
          <p className={styles.summaryReady}>
            <Check size={16} />
            {t('join.wizard.summary.ready')}
          </p>
        </Card>
      ) : null}

      <div className={styles.actions}>
        <Button variant="secondary" size="lg" disabled={step === 0} onClick={() => goTo(step - 1)}>
          {t('join.wizard.back')}
        </Button>
        <Button size="lg" className={styles.grow} disabled={save.isPending} onClick={next}>
          {save.isPending ? (
            <Spinner label={t('join.wizard.saving')} />
          ) : step === WIZARD_STEPS - 1 ? (
            t('join.wizard.submit')
          ) : (
            t('join.wizard.next')
          )}
        </Button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Ekran 2/4/6 — oczekiwanie na drugą stronę procesu
// ---------------------------------------------------------------------------

function WaitingScreen({
  title,
  body,
  messages,
}: {
  title: string
  body: string
  messages: OnboardingSessionResponse['messages']
}) {
  return (
    <div className={styles.centered}>
      <span className={styles.waitIcon} aria-hidden="true">
        <Clock size={34} />
      </span>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.centeredBody}>{body}</p>
      <MessageThread messages={messages} />
    </div>
  )
}

function MessageThread({ messages }: { messages: OnboardingSessionResponse['messages'] }) {
  const { t } = useTranslation()
  if (messages.length === 0) return null
  return (
    <Card className={styles.thread} pad="md">
      <p className={styles.threadTitle}>{t('join.terms.thread')}</p>
      {messages.map((message) => (
        <p key={`${message.createdAt}-${message.author}`} className={styles.threadRow}>
          <span className={styles.threadAuthor}>
            {t(message.author === 'hr' ? 'join.terms.authorHr' : 'join.terms.authorEmployee')}
          </span>
          <span className={styles.threadBody}>{message.body}</span>
        </p>
      ))}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Ekran 3 — warunki umowy
// ---------------------------------------------------------------------------

function TermsScreen({
  data,
  onChanged,
  onOpenDocument,
}: {
  data: OnboardingSessionResponse
  onChanged: () => void
  onOpenDocument: (key: OnboardingDocumentKey) => void
}) {
  const fmt = useFormat()
  const { t } = useTranslation()
  const questionId = useId()
  const [ackInfo, setAckInfo] = useState(false)
  const [ackRules, setAckRules] = useState(false)
  const [ackRodo, setAckRodo] = useState(false)
  const [question, setQuestion] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const terms = data.terms
  const rejected = data.sideState === 'rejected'
  const pendingQuestion = data.sideState === 'question'
  /*
   * Oświadczenie o regulaminie tylko przy opublikowanym regulaminie i umowie
   * o pracę (Fala 6, zmiana D8) — o dokumencie, którego nie ma, nie pytamy.
   */
  const rulesRequired = data.rulesAcknowledgement === 'required'
  /* Przy umowie cywilnej dokument to podsumowanie warunków umowy, nie informacja z art. 29 § 3 k.p. */
  const civil = data.rulesAcknowledgement === 'not_applicable'
  /* Warunki niosą inny wariant klauzuli RODO niż etap danych — potwierdzenie od nowa (przegląd Fali 6). */
  const rodoRenewal = data.rodoRenewal === true
  const acknowledged = ackInfo && (!rulesRequired || ackRules) && (!rodoRenewal || ackRodo)

  const decide = useMutation({
    mutationFn: async (action: 'accept' | 'reject' | 'ask'): Promise<void> => {
      if (action === 'accept') {
        await api.onboardingAcceptTerms(terms?.id ?? '', rulesRequired && ackRules, rodoRenewal && ackRodo)
      }
      else if (action === 'reject') await api.onboardingRejectTerms()
      else await api.onboardingAskQuestion(question ?? '')
    },
    onSuccess: () => {
      setQuestion(null)
      onChanged()
    },
    onError: (caught) => {
      /*
       * Kadry zmieniły ofertę, gdy była na ekranie: pokazujemy aktualną
       * i prosimy o decyzję od nowa — oświadczenia dotyczyły starej treści.
       */
      if (caught instanceof ApiError && caught.code === 'TERMS_CHANGED') {
        setAckInfo(false)
        setAckRules(false)
        setAckRodo(false)
        setError('join.terms.changed')
        onChanged()
        return
      }
      setError('join.errorGeneric')
    },
  })

  const rows = useMemo(() => {
    if (!terms) return []
    const contract = terms.contractType
      ? [
          t(`contractType.${terms.contractType}`),
          terms.probationMonths
            ? t('join.terms.probation', { months: terms.probationMonths })
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null
    const workplace = [terms.workplace, terms.workMode].filter(Boolean).join(' · ')
    /* Etat to pojęcie umowy o pracę — przy zleceniu, dziele i B2B go nie ma. */
    const employment = terms.contractType !== null && isEmploymentContract(terms.contractType)
    return [
      { key: 'position', value: terms.position },
      { key: 'department', value: data.employee.department },
      {
        key: 'fte',
        value: employment ? t('join.terms.fteValue', { fraction: workingTimeFraction(Number(terms.fte)) }) : null,
      },
      { key: 'startDate', value: terms.startDate ? fmt.date(terms.startDate, 'long') : null },
      { key: 'contractType', value: contract },
      {
        /* Umowa cywilna nie ma „wynagrodzenia zasadniczego", a kontrakt B2B — kwoty brutto (weryfikacja na żywo Fali 6). */
        key: employment || terms.contractType === null ? 'salary' : 'salaryCivil',
        value:
          terms.baseSalaryGr === null
            ? null
            : terms.contractType === 'b2b'
              ? formatPLN(terms.baseSalaryGr, { grosze: 'always' })
              : t('join.terms.gross', { amount: formatPLN(terms.baseSalaryGr, { grosze: 'always' }) }),
      },
      { key: 'bonus', value: terms.bonusNote },
      { key: 'workplace', value: workplace.length > 0 ? workplace : null },
    ].filter((row) => row.value !== null && row.value !== '')
  }, [terms, data.employee.department, t])

  return (
    <>
      <p className={styles.eyebrow}>{t('join.terms.eyebrow')}</p>
      <h1 className={styles.title}>{t(civil ? 'join.terms.titleCivil' : 'join.terms.title')}</h1>
      <p className={styles.lead}>{t('join.terms.lead')}</p>

      {rejected ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t('join.terms.rejected')}
        </InfoBanner>
      ) : null}
      {pendingQuestion ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('join.terms.questionPending')}
        </InfoBanner>
      ) : null}
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <Card className={styles.termsCard} pad="md">
        {rows.map((row) => (
          <p key={row.key} className={styles.termsRow}>
            <span className={styles.termsKey}>{t(`join.terms.rows.${row.key}`)}</span>
            <span className={styles.termsValue}>{row.value}</span>
          </p>
        ))}
      </Card>

      <Card className={styles.consentCard} pad="md">
        <Checkbox
          checked={ackInfo}
          disabled={rejected}
          onChange={(event) => setAckInfo(event.target.checked)}
          label={
            <span>
              {t(civil ? 'join.terms.ackInfoCivil' : 'join.terms.ackInfo')}{' '}
              <button
                type="button"
                className={styles.inlineLink}
                onClick={() => onOpenDocument('info')}
              >
                {t('join.terms.open')}
              </button>
            </span>
          }
        />
        {rulesRequired ? (
          <Checkbox
            checked={ackRules}
            disabled={rejected}
            onChange={(event) => setAckRules(event.target.checked)}
            label={
              <span>
                {t('join.terms.ackRules')}{' '}
                <button
                  type="button"
                  className={styles.inlineLink}
                  onClick={() => onOpenDocument('regulamin')}
                >
                  {t('join.terms.open')}
                </button>
              </span>
            }
          />
        ) : null}
        {rodoRenewal ? (
          <Checkbox
            checked={ackRodo}
            disabled={rejected}
            onChange={(event) => setAckRodo(event.target.checked)}
            label={
              <span>
                {t('join.terms.ackRodo')}{' '}
                <button
                  type="button"
                  className={styles.inlineLink}
                  onClick={() => onOpenDocument('rodo')}
                >
                  {t('join.terms.open')}
                </button>
              </span>
            }
          />
        ) : null}
        {data.rulesAcknowledgement === 'unavailable' ? (
          <p className={styles.ackHint}>{t('join.terms.rulesUnavailable')}</p>
        ) : null}
        {!acknowledged ? (
          <p className={styles.ackHint}>
            {t(rulesRequired ? 'join.terms.ackRequired' : 'join.terms.ackInfoRequired')}
          </p>
        ) : null}
      </Card>

      <MessageThread messages={data.messages} />

      {question === null ? (
        <div className={styles.decisions}>
          <Button
            size="lg"
            className={styles.grow}
            disabled={rejected || !acknowledged || decide.isPending}
            onClick={() => decide.mutate('accept')}
          >
            <Check size={18} />
            {t('join.terms.accept')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            disabled={rejected || pendingQuestion || decide.isPending}
            onClick={() => setQuestion('')}
          >
            {t('join.terms.ask')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className={styles.danger}
            disabled={rejected || decide.isPending}
            onClick={() => decide.mutate('reject')}
          >
            {t('join.terms.reject')}
          </Button>
        </div>
      ) : (
        <Card className={styles.askCard} pad="md">
          <FormField label={t('join.terms.askTitle')} htmlFor={questionId}>
            <textarea
              id={questionId}
              className={styles.textarea}
              rows={4}
              placeholder={t('join.terms.askPlaceholder')}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </FormField>
          <div className={styles.actions}>
            <Button variant="secondary" size="lg" onClick={() => setQuestion(null)}>
              {t('join.terms.cancel')}
            </Button>
            <Button
              size="lg"
              className={styles.grow}
              disabled={question.trim().length === 0 || decide.isPending}
              onClick={() => decide.mutate('ask')}
            >
              {t('join.terms.askSubmit')}
            </Button>
          </div>
        </Card>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Ekran 4 — badania lekarskie
// ---------------------------------------------------------------------------

function MedicalScreen({
  referralIssuedAt,
  startDate,
  onDone,
}: {
  /** Dzień wystawienia skierowania w obiegu — z sesji procesu (Fala 6, grupa 120). */
  referralIssuedAt: string | null
  /** Planowany dzień rozpoczęcia pracy — orzeczenie musi być ważne co najmniej do niego. */
  startDate: string | null
  onDone: () => void
}) {
  const fmt = useFormat()
  const { t } = useTranslation()
  const examId = useId()
  const validId = useId()
  const [examDate, setExamDate] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [serverIssues, setServerIssues] = useState<readonly string[]>([])

  const confirm = useMutation({
    mutationFn: () => api.onboardingConfirmMedical(examDate, validUntil),
    onSuccess: onDone,
    onError: (caught) => {
      /* Serwer nazywa zły zakres dat — pole mówi, co poprawić. */
      if (caught instanceof ApiError && caught.issues.length > 0) {
        setServerIssues(caught.issues)
        setError(null)
        return
      }
      setError('join.medical.error')
    },
  })

  /*
   * Te same granice co na serwerze: badanie nie w przyszłości, orzeczenie
   * ważne po dniu badania. Data sprzed skierowania nie blokuje — bywa
   * orzeczeniem od poprzedniego pracodawcy (art. 229 § 1(1) k.p.), więc
   * ekran ją tylko opisuje, a decyzja należy do działu HR.
   */
  const today = warsawDay(apiToday())
  const referralDay = referralIssuedAt ? warsawDay(new Date(referralIssuedAt)) : null
  const issues = new Set<string>(serverIssues)
  if (examDate !== '' && examDate > today) issues.add('exam_in_future')
  if (examDate !== '' && validUntil !== '' && validUntil <= examDate) {
    issues.add('valid_until_before_exam')
  }
  /* Ważne krócej niż do dnia dopuszczenia — BHP by go nie przyjęło (przegląd Fali 6). */
  const admissionDay = startDate !== null && startDate > today ? startDate : today
  if (validUntil !== '' && validUntil < admissionDay) issues.add('valid_until_before_start')
  const beforeReferral = referralDay !== null && examDate !== '' && examDate < referralDay
  const fieldError = (issue: string): string | undefined =>
    issues.has(issue)
      ? t(`join.medical.errors.${issue}`, { date: fmt.date(admissionDay, 'long') })
      : undefined

  const ready = examDate !== '' && validUntil !== '' && confirmed && issues.size === 0

  return (
    <>
      <p className={styles.eyebrow}>{t('join.medical.eyebrow')}</p>
      <h1 className={styles.title}>{t('join.medical.title')}</h1>
      <p className={styles.lead}>{t('join.medical.lead')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <Card className={styles.referralCard} pad="md">
        <span className={styles.referralIcon} aria-hidden="true">
          <Document size={22} />
        </span>
        <span className={styles.referralText}>
          <span className={styles.referralTitle}>{t('join.medical.referralTitle')}</span>
          {/* Plik pojawi się z modułem dokumentów — akcja jest wyłączona, nie udawana [D21]. */}
          <span className={styles.referralMeta}>{t('join.doc.downloadSoon')}</span>
        </span>
        <Button variant="secondary" size="sm" disabled>
          {t('join.medical.download')}
        </Button>
      </Card>

      <Card className={styles.confirmCard} pad="md">
        <p className={styles.confirmHeading}>{t('join.medical.confirmHeading')}</p>
        <div className={styles.fieldGrid}>
          <FormField
            label={t('join.medical.examDate')}
            htmlFor={examId}
            error={fieldError('exam_in_future')}
          >
            <Input
              type="date"
              max={today}
              value={examDate}
              onChange={(event) => {
                setExamDate(event.target.value)
                setServerIssues([])
              }}
            />
          </FormField>
          <FormField
            label={t('join.medical.validUntil')}
            htmlFor={validId}
            error={fieldError('valid_until_before_exam') ?? fieldError('valid_until_before_start')}
          >
            <Input
              type="date"
              {...(examDate !== '' ? { min: shiftDay(examDate, 1) } : {})}
              value={validUntil}
              onChange={(event) => {
                setValidUntil(event.target.value)
                setServerIssues([])
              }}
            />
          </FormField>
        </div>
        {beforeReferral ? (
          <InfoBanner tone="info" className={styles.banner}>
            {t('join.medical.beforeReferral', { date: fmt.date(referralDay, 'long') })}
          </InfoBanner>
        ) : null}
        <Checkbox
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          label={t('join.medical.confirm')}
        />
        <Button
          size="lg"
          className={styles.fullButton}
          disabled={!ready || confirm.isPending}
          onClick={() => confirm.mutate()}
        >
          {confirm.isPending ? <Spinner label={t('common.loading')} /> : t('join.medical.submit')}
        </Button>
      </Card>
    </>
  )
}

// ---------------------------------------------------------------------------
// Ekran 5 — zakończenie
// ---------------------------------------------------------------------------

function DoneScreen({ firstName }: { firstName: string }) {
  const { t } = useTranslation()
  const fmt = useFormat()
  const name = greetingName(fmt.lng, firstName)
  return (
    <div className={styles.centered}>
      <span className={styles.doneIcon} aria-hidden="true">
        <Check size={38} />
      </span>
      <h1 className={styles.title}>
        {name === null ? t('join.done.titleNeutral') : t('join.done.title', { name })}
      </h1>
      <p className={styles.centeredBody}>
        {t('join.done.body')} {t('join.done.statusPrefix')}{' '}
        <strong className={styles.statusActive}>{t('join.done.statusActive')}</strong>.{' '}
        {t('join.done.statusSuffix')}
      </p>
      <p className={styles.doneExtra}>{t('join.done.extra')}</p>
      <Link to="/login">
        <Button size="lg">{t('join.done.goToPortal')}</Button>
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal dokumentu
// ---------------------------------------------------------------------------

function DocumentModal({
  documentKey,
  onClose,
}: {
  documentKey: OnboardingDocumentKey | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const document = useQuery({
    queryKey: ['onboarding', 'document', documentKey],
    queryFn: () => api.onboardingDocument(documentKey!),
    enabled: documentKey !== null,
  })

  return (
    <Modal
      open={documentKey !== null}
      onClose={onClose}
      title={
        <span className={styles.docHeader}>
          <Badge>{t('join.doc.badge')}</Badge>
          {document.data?.title ?? t('common.loading')}
        </span>
      }
      footer={
        <div className={styles.docFooter}>
          <span className={styles.docNote}>{t('join.doc.downloadSoon')}</span>
          <Button variant="secondary" disabled>
            {t('join.doc.download')}
          </Button>
        </div>
      }
    >
      {document.isLoading ? <Spinner label={t('common.loading')} /> : null}
      {document.data?.status === 'unavailable' ? (
        <InfoBanner tone="warning">{document.data.note}</InfoBanner>
      ) : null}
      {document.data?.sections.map((section, index) => (
        <section key={section.heading ?? index} className={styles.docSection}>
          {section.heading !== null ? (
            <h3 className={styles.docHeading}>{section.heading}</h3>
          ) : null}
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className={cx(styles.docParagraph)}>
              {paragraph}
            </p>
          ))}
        </section>
      ))}
      {document.data ? (
        <p className={styles.docVersion}>
          {t('join.doc.version', { version: document.data.version })}
        </p>
      ) : null}
    </Modal>
  )
}
